// Chat Context - Sohbet state yönetimi
import { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  // Conversations
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Assistants
  const [selectedAssistant, setSelectedAssistant] = useState("kaan");
  const [selectedCustomAssistant, setSelectedCustomAssistant] = useState(null);
  const [customAssistants, setCustomAssistants] = useState([]);
  
  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  
  // Mode
  const [mode, setMode] = useState("chat");
  const [voiceEnabled, setVoiceEnabled] = useState(false);

  // Load conversations
  const loadConversations = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/chat/conversations?user_id=${userId}`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error("Conversations load error:", error);
    }
  }, []);

  // Load custom assistants
  const loadCustomAssistants = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/assistants?user_id=${userId}`);
      setCustomAssistants(response.data.assistants || []);
    } catch (error) {
      console.error("Custom assistants load error:", error);
    }
  }, []);

  // Create new conversation
  const createNewConversation = useCallback(async (userId, title = "Yeni Sohbet") => {
    try {
      const response = await axios.post(`${API}/chat/conversations`, {
        user_id: userId,
        title,
        assistant_type: selectedCustomAssistant?.id || selectedAssistant,
        personality: selectedCustomAssistant ? "custom" : "default"
      });
      
      const newConv = response.data;
      setConversations(prev => [newConv, ...prev]);
      setActiveConversation(newConv);
      setMessages([]);
      return newConv;
    } catch (error) {
      console.error("Create conversation error:", error);
      toast.error("Sohbet oluşturulamadı");
      return null;
    }
  }, [selectedAssistant, selectedCustomAssistant]);

  // Select conversation
  const selectConversation = useCallback(async (conversation) => {
    setActiveConversation(conversation);
    setMessages(conversation.messages || []);
    
    // Set assistant based on conversation
    if (conversation.assistant_type) {
      if (conversation.assistant_type === "kaan" || conversation.assistant_type === "defne") {
        setSelectedAssistant(conversation.assistant_type);
        setSelectedCustomAssistant(null);
      } else {
        const customAssistant = customAssistants.find(ca => ca.id === conversation.assistant_type);
        if (customAssistant) {
          setSelectedCustomAssistant(customAssistant);
        }
      }
    }
  }, [customAssistants]);

  // Select default assistant
  const selectDefaultAssistant = useCallback((assistant) => {
    setSelectedAssistant(assistant);
    setSelectedCustomAssistant(null);
  }, []);

  // Select custom assistant
  const selectCustomAssistant = useCallback((assistant) => {
    setSelectedCustomAssistant(assistant);
  }, []);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      await axios.delete(`${API}/chat/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Delete conversation error:", error);
      toast.error("Sohbet silinemedi");
    }
  }, [activeConversation]);

  // Archive conversation
  const archiveConversation = useCallback(async (conversationId) => {
    try {
      await axios.put(`${API}/chat/conversations/${conversationId}`, { is_archived: true });
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Archive conversation error:", error);
      toast.error("Sohbet arşivlenemedi");
    }
  }, [activeConversation]);

  // Go home (close current conversation)
  const goHome = useCallback(() => {
    setActiveConversation(null);
    setMessages([]);
  }, []);

  // Add message
  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  // Update message
  const updateMessage = useCallback((messageId, updates) => {
    setMessages(prev => prev.map(m => 
      m.id === messageId ? { ...m, ...updates } : m
    ));
  }, []);

  // Delete message
  const deleteMessage = useCallback(async (messageId) => {
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (error) {
      console.error("Delete message error:", error);
    }
  }, []);

  const value = {
    // Conversations
    conversations,
    setConversations,
    activeConversation,
    setActiveConversation,
    messages,
    setMessages,
    
    // Assistants
    selectedAssistant,
    setSelectedAssistant,
    selectedCustomAssistant,
    setSelectedCustomAssistant,
    customAssistants,
    setCustomAssistants,
    
    // UI State
    isLoading,
    setIsLoading,
    streamingText,
    setStreamingText,
    
    // Mode
    mode,
    setMode,
    voiceEnabled,
    setVoiceEnabled,
    
    // Actions
    loadConversations,
    loadCustomAssistants,
    createNewConversation,
    selectConversation,
    selectDefaultAssistant,
    selectCustomAssistant,
    deleteConversation,
    archiveConversation,
    goHome,
    addMessage,
    updateMessage,
    deleteMessage
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}

export default ChatContext;
